import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { getCompanySettings } from '../lib/companySettings'

const Footer = () => {
  const [settings, setSettings] = useState(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const data = await getCompanySettings(supabase)
      if (mounted) setSettings(data)
    })()
    return () => { mounted = false }
  }, [])

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={settings.website_name || 'Logo'} className="h-8 w-8 rounded-lg object-cover" />
              ) : (
                <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{(settings?.website_name || 'OM').slice(0,2)}</span>
                </div>
              )}
              <span className="ml-2 text-xl font-bold">{settings?.website_name || 'One Matrix'}</span>
            </div>
            <p className="text-gray-300 mb-4">
              {settings?.footer_about || (settings?.company_name ? `${settings.company_name} — ` : '') + 'We provide comprehensive IT solutions, mobile and web development, and digital services.'}
            </p>
            <div className="flex space-x-4">
              {settings?.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
              )}
              {settings?.linkedin_url && (
                <a href={settings.linkedin_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
              {settings?.twitter_url && (
                <a href={settings.twitter_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                  </svg>
                </a>
              )}
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm5.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z"/>
                  </svg>
                </a>
              )}
              {settings?.youtube_url && (
                <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">YouTube</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a2.993 2.993 0 00-2.107-2.12C19.59 3.5 12 3.5 12 3.5s-7.59 0-9.391.566a2.993 2.993 0 00-2.107 2.12C0 8.004 0 12 0 12s0 3.996.502 5.814a2.993 2.993 0 002.107 2.12C4.41 20.5 12 20.5 12 20.5s7.59 0 9.391-.566a2.993 2.993 0 002.107-2.12C24 15.996 24 12 24 12s0-3.996-.502-5.814zM9.75 15.5v-7l6 3.5-6 3.5z"/>
                  </svg>
                </a>
              )}
              {settings?.github_url && (
                <a href={settings.github_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">GitHub</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 .5C5.73.5.89 5.34.89 11.61c0 4.88 3.16 9.02 7.54 10.48.55.1.75-.24.75-.53 0-.26-.01-1.13-.02-2.05-3.07.67-3.72-1.3-3.72-1.3-.5-1.26-1.22-1.6-1.22-1.6-.99-.67.08-.66.08-.66 1.1.08 1.68 1.13 1.68 1.13.98 1.68 2.58 1.2 3.21.92.1-.71.38-1.2.69-1.48-2.45-.28-5.02-1.22-5.02-5.43 0-1.2.43-2.18 1.13-2.95-.11-.28-.49-1.42.11-2.96 0 0 .92-.29 3.02 1.13a10.48 10.48 0 015.5 0c2.1-1.42 3.02-1.13 3.02-1.13.6 1.54.22 2.68.11 2.96.7.77 1.13 1.75 1.13 2.95 0 4.22-2.58 5.14-5.04 5.41.4.35.75 1.05.75 2.12 0 1.53-.01 2.76-.01 3.14 0 .29.2.64.76.53 4.37-1.46 7.53-5.6 7.53-10.48C23.11 5.34 18.27.5 12 .5z"/>
                  </svg>
                </a>
              )}
              {settings?.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">
                  <span className="sr-only">WhatsApp</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.004 2c-5.514 0-9.996 4.482-9.996 9.996 0 1.764.463 3.478 1.342 4.998L2 22l5.13-1.333a9.95 9.95 0 004.874 1.29h.001c5.514 0 9.996-4.482 9.996-9.996S17.518 2 12.004 2zm5.823 14.315c-.245.695-1.411 1.31-1.94 1.397-.518.082-1.177.116-1.897-.118-.438-.139-1.002-.325-1.731-.635-3.046-1.313-5.03-4.376-5.184-4.585-.152-.21-1.239-1.651-1.239-3.157 0-1.506.786-2.242 1.063-2.548.277-.305.604-.382.806-.382.201 0 .403.002.581.01.186.009.438-.071.686.523.245.595.83 2.055.905 2.203.074.148.123.321.023.53-.098.21-.148.34-.293.523-.148.183-.31.408-.44.55-.145.148-.296.311-.128.61.168.298.75 1.235 1.611 2.002 1.11.99 2.046 1.296 2.344 1.444.298.148.474.123.649-.074.174-.197.747-.87.948-1.17.201-.3.402-.248.672-.148.27.099 1.715.81 2.01.955.296.147.493.219.565.339.074.119.074.695-.171 1.39z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="/services" className="text-gray-300 hover:text-white">Services</a></li>
              <li><a href="/products" className="text-gray-300 hover:text-white">Products</a></li>
              <li><a href="/contact" className="text-gray-300 hover:text-white">Contact</a></li>
              <li><a href="/appointment" className="text-gray-300 hover:text-white">Book Appointment</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <div className="space-y-2 text-gray-300">
              <p>Email: {settings?.email || 'info@onematrix.com'}</p>
              <p>Phone: {settings?.phone || '+880'}</p>
              <p>Address: {settings?.address || 'Dhaka, Bangladesh'}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} {settings?.company_name || 'One Matrix'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
